// ---------------------------------------------------------------------------
// De un error de red a un motivo que la pantalla puede afirmar sin mentir.
//
// El pecado que esto corrige: una sola clave de error para cualquier fallo, y
// encima una clave que asegura la causa («solo un administrador puede pedirla»)
// sin haber mirado la respuesta. En la Bitácora eso llegó a leerse cuando el
// servidor había respondido 201 y los resúmenes ya estaban escritos.
//
// Reglas, en el orden en que se aplican:
//   · Hay respuesta del servidor → manda el código. 403 es el ÚNICO caso donde
//     se puede hablar de permisos; 401 es sesión; cualquier otro es un fallo
//     genérico, sin causa inventada.
//   · No hay respuesta (red caída, petición abortada, se agotó el tiempo de
//     espera del cliente) → `unconfirmed`. No sabemos si el servidor terminó el
//     trabajo, así que la pantalla no puede decir que NO se hizo.
//
// Y si el servidor mandó un mensaje, ese mensaje gana: es la única causa que
// alguien verificó de verdad.
// ---------------------------------------------------------------------------

export type RequestFailureKind =
  /** 403: el servidor dijo que no, por permisos. */
  | "permission"
  /** 401: la sesión no vale (o ya no vale). */
  | "auth"
  /** Sin respuesta: puede haber terminado bien del otro lado. No se puede negar el resultado. */
  | "unconfirmed"
  /** Cualquier otro fallo con respuesta: se dice que falló, sin atribuirle una causa. */
  | "failed";

export interface RequestFailure {
  kind: RequestFailureKind;
  /** Código HTTP, o `null` si la respuesta nunca llegó. */
  status: number | null;
  /** Mensaje del servidor, si mandó uno utilizable. */
  serverMessage: string | null;
}

/** Códigos de axios/fetch que significan «esto no llegó a tener respuesta». */
const NO_RESPONSE_CODES = new Set(["ECONNABORTED", "ETIMEDOUT", "ERR_NETWORK", "ERR_CANCELED", "ECONNRESET", "ERR_INTERNET_DISCONNECTED"]);
const NO_RESPONSE_NAMES = new Set(["AbortError", "TimeoutError", "CanceledError", "NetworkError"]);

/** Un mensaje del servidor solo sirve si es texto corto y no una página de error. */
const usableMessage = (value: unknown): string | null => {
  if (typeof value !== "string") return null;
  const text = value.trim();
  if (!text || text.length > 300) return null;
  if (text.startsWith("<")) return null; // HTML de un proxy, no un motivo.
  return text;
};

/** El backend responde `{ meta, data }`; otros errores traen `message` o `error` a secas. */
const serverMessageOf = (data: unknown): string | null => {
  const direct = usableMessage(data);
  if (direct) return direct;
  if (!data || typeof data !== "object") return null;
  const d = data as { meta?: { message?: unknown }; message?: unknown; error?: unknown };
  return usableMessage(d.meta?.message) ?? usableMessage(d.message) ?? usableMessage(d.error);
};

/**
 * Clasifica el error de una llamada HTTP. Es a propósito «pato»: no importa si
 * viene de axios, de `fetch` o de un mock — solo su forma. Un error que no se
 * reconoce cae en `failed`, nunca en `permission`.
 */
export function describeRequestFailure(error: unknown): RequestFailure {
  const e = (error ?? {}) as {
    response?: { status?: unknown; data?: unknown };
    status?: unknown;
    code?: unknown;
    name?: unknown;
    message?: unknown;
  };

  const rawStatus = typeof e.response?.status === "number" ? e.response.status : typeof e.status === "number" ? e.status : null;
  const serverMessage = e.response ? serverMessageOf(e.response.data) : null;

  if (rawStatus !== null) {
    const kind: RequestFailureKind = rawStatus === 403 ? "permission" : rawStatus === 401 ? "auth" : "failed";
    return { kind, status: rawStatus, serverMessage };
  }

  // Hubo respuesta, pero sin código utilizable. No quedó en el aire —el
  // servidor contestó—, así que es un fallo a secas; y si mandó un motivo, ese
  // motivo no se tira: es la única causa que alguien verificó.
  if (e.response) return { kind: "failed", status: null, serverMessage };

  const code = typeof e.code === "string" ? e.code : "";
  const name = typeof e.name === "string" ? e.name : "";
  const message = typeof e.message === "string" ? e.message.toLowerCase() : "";
  const noResponse =
    NO_RESPONSE_CODES.has(code) ||
    NO_RESPONSE_NAMES.has(name) ||
    /timeout|timed out|network error|aborted|canceled|cancelled/.test(message);

  if (noResponse) return { kind: "unconfirmed", status: null, serverMessage: null };
  return { kind: "failed", status: null, serverMessage: null };
}

/** `true` cuando el resultado quedó en el aire: hay que releer antes de concluir nada. */
export const isUnconfirmed = (failure: RequestFailure): boolean => failure.kind === "unconfirmed";

// ---------------------------------------------------------------------------
// De la clasificación al texto. Cada sección declara SUS claves —una por
// motivo— y este ayudante elige la que toca. Se guarda la clave y sus
// variables, nunca el texto ya traducido: así el aviso puede sobrevivir a un
// cambio de idioma y `load` no necesita depender de `t`.
//
// Lo que este ayudante garantiza, y por lo que existe: la clave de permisos
// SOLO puede salir de un 403, y la de «sin confirmar» SOLO de un fallo sin
// respuesta. No hay camino desde un 500 hasta «no tienes permiso».
// ---------------------------------------------------------------------------

/** Texto i18n (clave + variables) que se puede guardar en el estado sin congelar el idioma. */
export interface FailureMessage {
  key: string;
  opts?: Record<string, unknown>;
}

/** Las claves i18n de una sección, una por motivo. `server` recibe `{{message}}`. */
export interface FailureKeys {
  /** Fallo con respuesta del servidor: se dice que falló, sin atribuirle causa. */
  failed: string;
  /** SOLO para 403. */
  permission: string;
  /** SOLO para 401. */
  auth: string;
  /** SOLO cuando no hubo respuesta: el resultado no se puede negar. */
  unconfirmed: string;
  /** Opcional: el motivo que mandó el servidor, que gana sobre todo lo demás. */
  server?: string;
}

/** Elige la clave i18n que corresponde al fallo; `opts` viaja a todas por igual. */
export function failureMessage(
  failure: RequestFailure,
  keys: FailureKeys,
  opts?: Record<string, unknown>,
): FailureMessage {
  if (keys.server && failure.serverMessage) return { key: keys.server, opts: { ...opts, message: failure.serverMessage } };
  if (failure.kind === "permission") return { key: keys.permission, opts };
  if (failure.kind === "auth") return { key: keys.auth, opts };
  if (failure.kind === "unconfirmed") return { key: keys.unconfirmed, opts };
  return { key: keys.failed, opts };
}

/** Atajo para las lecturas, donde solo importa distinguir el corte por tiempo. */
export const readFailureKey = (error: unknown, keys: { failed: string; timeout: string }): string =>
  isUnconfirmed(describeRequestFailure(error)) ? keys.timeout : keys.failed;
